import { useState, useEffect } from 'react';
import { reportApi } from '../services/api';
import './UndoModal.css';

const UndoModal = ({ isOpen, onClose, onUndoSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [error, setError] = useState(null);

  const handleOpen = async () => {
    if (!isOpen) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await reportApi.getLastBatch();
      setLastBatch(response.data.batch);
    } catch (err) {
      console.error('Failed to load last batch:', err);
      setError('Failed to load recent logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUndo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportApi.undoLastBatch();
      onUndoSuccess(response.data.message, response.data.summary);
      onClose();
    } catch (err) {
      console.error('Failed to undo reports:', err);
      setError(err.response?.data?.error || 'Failed to undo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load batch data when modal opens
  useEffect(() => {
    if (isOpen) {
      handleOpen();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Undo last log</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading recent logs...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && !lastBatch && (
            <div className="no-reports">
              <p>No recent logs to undo.</p>
            </div>
          )}

          {!loading && !error && lastBatch && lastBatch.length > 0 && (
            <div className="batch-preview">
              <p className="batch-description">
                This will undo your most recent log of <strong>{lastBatch.length}</strong> chapter{lastBatch.length === 1 ? '' : 's'}:
              </p>
              
              <div className="batch-details">
                <div className="detail-section">
                  <h4>Readers:</h4>
                  <ul>
                    {[...new Set(lastBatch.map(r => r.reader.name))].map(name => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>

                <div className="detail-section">
                  <h4>Books:</h4>
                  <ul>
                    {[...new Set(lastBatch.map(r => r.chapter.book))].map(book => (
                      <li key={book}>{book}</li>
                    ))}
                  </ul>
                </div>

                <div className="detail-section">
                  <h4>Date:</h4>
                  <p>{new Date(lastBatch[0].readDate).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="warning-message">
                <p><strong>⚠️ Warning:</strong> This action cannot be undone. All {lastBatch.length} log{lastBatch.length === 1 ? '' : 's'} will be permanently deleted.</p>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="cancel-button" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="undo-button" 
            onClick={handleUndo}
            disabled={loading || !lastBatch || lastBatch.length === 0}
          >
            {loading ? 'Undoing...' : 'Undo logs'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UndoModal;
